import React, { useState, useCallback } from "react";
import { size } from "lodash";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import ChatIcon from "@mui/icons-material/Chat";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Iconify from "../iconify/iconify";

import EmptyIcon from "../../assets/icons/empty/ic_content.svg";

import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";

export default function List({
  changeData,
  loading,
  data,
  paginationData,
  columnsData,
  getRowId,
  autoHeight,
  enableFollowup = false,
  enableEdit = false,
  enableView = false,
  enableChat = false,
  enableApprove = false,
  enableDelete = false,
  disableDelete,
  getUniqColName = "id",
  unlimitedData = false,
  onFollowUpIconPress = () => {},
  onEditIconPress = () => {},
  onViewIconPress = () => {},
  onChatIconPress = () => {},
  onDeleteIconpress = () => {},
  onApproveIconPress = () => {},
  onPageChange = () => {},
  checkboxSelection,
  rowsPerPageOnChange,
  onRowSelectionModelChange,
  paginationRequired = true,
  defaultRowCount = rowsPerPageOnChange ?? 15,
  defaultDense = 56,
  actionsAtEnd = "",
  disableRowSelectionOnClick = false,
  rowSelectionModel,
}) {
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowCount);
  const [dense, setDense] = useState(defaultDense);
  const denseHeight = dense ? 56 : 76; // give non-dense a bit more room

  const onChangeDense = useCallback((event) => {
    setDense(event.target.checked);
  }, []);

  const cols = [...columnsData]; // fresh each render (no duplication)

  const MyCustomNoRowsOverlay = () => (
    <Stack mt={5} alignItems="center" justifyContent="center">
      <Box
        component="img"
        alt="empty content"
        src={EmptyIcon}
        sx={{ width: 1, maxWidth: 160 }}
      />
      <Typography
        variant="h6"
        component="span"
        sx={{ mt: 1, color: "text.disabled", textAlign: "center" }}
      >
        {"No Data"}
      </Typography>
    </Stack>
  );

  if (loading) {
    return <></>;
  } else if (!size(data)) {
    return (
      <DataGrid
        rowHeight={150}
        headerHeight={50}
        columns={cols}
        rows={[]}
        disableColumnMenu
        autoHeight={autoHeight}
        pageSize={0}
        hideFooterPagination={true}
        getRowId={getRowId}
        sx={{
          height: 400,
          border: "none",
          "& .MuiDataGrid-container--top [role=row]": {
            background: "#f4f6f8 !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "0.4em",
            height: "0.4em",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            borderRadius: 5,
          },
        }}
        slots={{ noRowsOverlay: MyCustomNoRowsOverlay }}
      />
    );
  }

  const handleRowsPerPageChange = async (event) => {
    setRowsPerPage(event.target.value);
    await onPageChange(1, event.target.value);
  };

  // Approve column (unique field name)
  if (enableApprove) {
    cols.splice(0, 0, {
      field: "approve",
      headerName: "Approve",
      sortable: false,
      minWidth: 80,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ cursor: "pointer" }}
          >
            <MatApprove
              rowId={getRowId(params.row)}
              onApproveIconPress={() => {
                onApproveIconPress(getRowId(params.row));
              }}
            />
          </div>
        );
      },
    });
  }

  // Chat column (unique field name)
  if (enableChat) {
    cols.splice(1, 0, {
      field: "chat",
      headerName: "Chat",
      sortable: false,
      minWidth: 80,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ cursor: "pointer" }}
          >
            <MatChat
              rowId={getRowId(params.row)}
              onChatIconPress={() => {
                onChatIconPress(getRowId(params.row));
              }}
            />
          </div>
        );
      },
    });
  }

  // Row actions (menu or inline) — build dynamically
  if (enableView || enableFollowup || enableEdit || enableDelete) {
    const buildMenuActions = (params) => {
      const items = [];

      if (enableView) {
        items.push(
          <GridActionsCellItem
            showInMenu
            icon={<Iconify icon="solar:eye-bold" />}
            label="View"
            onClick={() => onViewIconPress(getRowId(params.row))}
            key="view"
          />
        );
      }

      if (enableFollowup) {
        items.push(
          <GridActionsCellItem
            showInMenu
            icon={<Iconify icon="fluent:mail-24-filled" />}
            label="Follow Up"
            onClick={() => onFollowUpIconPress(getRowId(params.row))}
            key="followup"
          />
        );
      }

      if (enableEdit) {
        items.push(
          <GridActionsCellItem
            showInMenu
            icon={<Iconify icon="solar:pen-bold" />}
            label="Edit"
            onClick={() => onEditIconPress(getRowId(params.row))}
            key="edit"
          />
        );
      }

      const canDelete =
        enableDelete && !(disableDelete === true || params?.row?.status === 2);

      if (canDelete) {
        items.push(
          <GridActionsCellItem
            showInMenu
            icon={<Iconify icon="solar:trash-bin-trash-bold" />}
            label="Delete"
            onClick={() => onDeleteIconpress(params.row)} // passes entire row (as in your usage)
            sx={{ color: "error.main" }}
            key="delete"
          />
        );
      }

      return items;
    };

    if (actionsAtEnd === "push") {
      // inline icon buttons
      cols.push({
        field: "rowActionsInline",
        headerName: "Actions",
        sortable: false,
        minWidth: 120,
        disableClickEventBubbling: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            {enableView && (
              <MatView
                onViewIconPress={() => onViewIconPress(getRowId(params.row))}
              />
            )}
            {enableFollowup && (
              <IconButton
                size="small"
                onClick={() => onFollowUpIconPress(getRowId(params.row))}
              >
                <Iconify icon="fluent:mail-24-filled" />
              </IconButton>
            )}
            {enableEdit && (
              <IconButton
                size="small"
                onClick={() => onEditIconPress(getRowId(params.row))}
              >
                <Iconify icon="solar:pen-bold" />
              </IconButton>
            )}
            {enableDelete &&
              !(disableDelete === true || params?.row?.status === 2) && (
                <IconButton
                  size="small"
                  onClick={() => onDeleteIconpress(params.row)}
                  sx={{ color: "error.main" }}
                >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              )}
          </Stack>
        ),
      });
    } else {
      // menu actions column
      cols.splice(cols.length, 0, {
        type: "actions",
        field: "rowActions", // unique field name
        headerName: "Actions",
        sortable: false,
        minWidth: 80,
        disableClickEventBubbling: true,
        getActions: buildMenuActions,
      });
    }
  }

  return (
    <Box
      sx={{
        "& .cold": {
          color: "#00B8D9",
          backgroundColor: "rgba(0, 184, 217, 0.16)",
          fontSize: "0.75rem",
          fontWeight: 700,
          display: "flex",
          width: "100%",
        },
        "& .hot": {
          color: "#FFAC82",
          backgroundColor: "rgba(255, 86, 48, 0.16)",
          fontSize: "0.75rem",
          fontWeight: 700,
        },
        "& .warm": {
          color: "#FFD666",
          backgroundColor: "rgba(255, 171, 0, 0.16)",
          fontSize: "0.75rem",
          fontWeight: 700,
        },
      }}
    >
      <DataGrid
        columnVisibilityModel={{
          id: false, // hide 'id' column (optional)
        }}
        rowHeight={40}
        headerHeight={50}
        rows={data}
        columns={cols}
        disableColumnMenu
        getRowHeight={() => denseHeight}
        // autoHeight (if you truly want it, pass autoHeight directly to DataGrid)
        autoHeight={autoHeight}
        getRowId={getRowId}
        RowIdColName={getUniqColName}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        onRowSelectionModelChange={onRowSelectionModelChange}
        pageSize={rowsPerPage}
        hideFooter={unlimitedData === true ? false : true}
        rowSelectionModel={rowSelectionModel}
        checkboxSelection={checkboxSelection}
        getCellClassName={(params) => {
          if (params.field === "inquiry_status_val") {
            if (params.row?.inquiry_status_val === "Hot") {
              return "hot";
            } else if (params.row?.inquiry_status_val === "Cold") {
              return "cold";
            } else if (params.row?.inquiry_status_val === "Warm") {
              return "warm";
            }
          }
          return "";
        }}
        sx={{
          height: 450,
          border: "none",
          "& .MuiDataGrid-container--top [role=row], & .MuiDataGrid-container--bottom":
            {
              background: "#f4f6f8 !important",
            },
          "& .MuiDataGrid-columnHeaders": {
            color: "rgb(133, 141, 148)",
            fontFamily: "revert",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "0.4em",
            height: "0.4em",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            borderRadius: 5,
            background: "grey",
          },
        }}
        slots={{
          toolbar: () => <></>,
        }}
      />

      {paginationRequired === true && !!size(data) ? (
        <Grid
          sx={{ display: "flex", justifyContent: "space-between", padding: 2 }}
        >
          <Grid sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ mr: 1 }}>Dense:</Typography>
            {onChangeDense && (
              <FormControlLabel
                control={<Switch checked={dense} onChange={onChangeDense} />}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "blue",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "blue",
                  },
                  mr: 1,
                }}
              />
            )}
            <Typography sx={{ mr: 1 }}>Rows per page:</Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              <MenuItem defaultValue={15} value={15}>
                15
              </MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <Grid>
              <Typography sx={{ ml: 2 }}>
                Total Records: {Number(paginationData.total_count)}
              </Typography>
            </Grid>
          </Grid>
          <Pagination
            page={Number(paginationData.current_page)}
            count={Number(paginationData.total_page)}
            variant="outlined"
            shape="rounded"
            onChange={async (e, page) => {
              await onPageChange(page, rowsPerPage);
            }}
            sx={{ alignItems: "center" }}
          />
        </Grid>
      ) : null}
    </Box>
  );
}

/* ---- Small leaf components ---- */

const MatApprove = ({ onApproveIconPress }) => {
  return (
    <IconButton aria-label="approve" size="small" onClick={onApproveIconPress}>
      <CheckCircleOutlineIcon fontSize="small" />
    </IconButton>
  );
};

const MatView = ({ onViewIconPress }) => {
  return (
    <IconButton aria-label="view" size="small" onClick={onViewIconPress}>
      <Iconify icon="solar:eye-bold" />
    </IconButton>
  );
};

const MatChat = ({ onChatIconPress }) => {
  return (
    <IconButton aria-label="chat" size="small" onClick={onChatIconPress}>
      <ChatIcon fontSize="small" />
    </IconButton>
  );
};
